import React, { useState } from "react";
import { Plus, Trash2, Pencil, Loader2, Save, X, Users } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const ClientManager = ({ clients, onChanged }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [limit, setLimit] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({ name: "", phone: "", limit_kredit: 0 });
  const [credit, setCredit] = useState({});

  React.useEffect(() => {
    let alive = true;
    Promise.all(
      clients.map((c) =>
        api
          .get(`/clients/${c.id}/credit`)
          .then((r) => [c.id, r.data])
          .catch(() => [c.id, null])
      )
    ).then((pairs) => {
      if (alive) setCredit(Object.fromEntries(pairs.filter(([, v]) => v)));
    });
    return () => {
      alive = false;
    };
  }, [clients]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error("Nama dan nomor HP wajib diisi");
      return;
    }
    setSaving(true);
    try {
      await api.post("/clients", {
        name: name.trim(),
        phone: phone.trim(),
        limit_kredit: Number(limit) || 0,
      });
      toast.success("Client ditambahkan");
      setName("");
      setPhone("");
      setLimit("");
      onChanged?.();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Gagal menambah client");
    } finally {
      setSaving(false);
    }
  };

  const save = async (id) => {
    try {
      await api.put(`/clients/${id}`, { ...draft, limit_kredit: Number(draft.limit_kredit) || 0 });
      toast.success("Client diperbarui");
      setEditId(null);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Hapus client ${c.name}?`)) return;
    await api.delete(`/clients/${c.id}`);
    toast.success("Client dihapus");
    onChanged?.();
  };

  return (
    <div className="glass rounded-2xl p-6 md:p-8" data-testid="client-manager">
      <div className="flex items-center gap-3">
        <Users className="w-5 h-5 text-blue-400" />
        <h3 className="font-display text-xl font-semibold">
          Data Client{" "}
          <span className="text-slate-500 text-sm font-normal">({clients.length})</span>
        </h3>
      </div>

      <form onSubmit={add} className="mt-6 flex flex-wrap gap-3">
        <Input
          data-testid="client-name-input"
          placeholder="Nama client"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="h-11 flex-1 min-w-[180px] bg-slate-900/70 border-white/10"
        />
        <Input
          data-testid="client-phone-input"
          placeholder="Nomor HP"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="h-11 flex-1 min-w-[160px] bg-slate-900/70 border-white/10"
        />
        <Input
          data-testid="client-limit-input"
          placeholder="Limit kredit (0 = tanpa batas)"
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          className="h-11 flex-1 min-w-[170px] bg-slate-900/70 border-white/10"
        />
        <Button
          data-testid="client-add-btn"
          type="submit"
          disabled={saving}
          className="h-11 rounded-xl bg-blue-600 hover:bg-blue-500 px-6 font-semibold"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-2" /> Tambah</>}
        </Button>
      </form>

      <div className="mt-7 divide-y divide-white/5" data-testid="client-list">
        {clients.length === 0 && (
          <p className="py-8 text-center text-slate-500 text-sm">Belum ada data client.</p>
        )}
        {clients.map((c) => (
          <div
            key={c.id}
            data-testid={`client-row-${c.id}`}
            className="py-4 flex flex-wrap items-center gap-3 justify-between"
          >
            {editId === c.id ? (
              <div className="flex flex-wrap gap-3 flex-1">
                <Input
                  data-testid="client-edit-name"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  className="h-10 flex-1 min-w-[150px] bg-slate-900/70 border-white/10"
                />
                <Input
                  data-testid="client-edit-phone"
                  value={draft.phone}
                  onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                  className="h-10 flex-1 min-w-[140px] bg-slate-900/70 border-white/10"
                />
                <Input
                  data-testid="client-edit-limit"
                  value={draft.limit_kredit}
                  onChange={(e) => setDraft({ ...draft, limit_kredit: e.target.value })}
                  placeholder="Limit kredit"
                  className="h-10 flex-1 min-w-[130px] bg-slate-900/70 border-white/10"
                />
                <Button data-testid="client-save-btn" onClick={() => save(c.id)} className="h-10 bg-blue-600 hover:bg-blue-500">
                  <Save className="w-4 h-4" />
                </Button>
                <Button data-testid="client-cancel-btn" onClick={() => setEditId(null)} variant="outline" className="h-10 border-white/10 bg-slate-800/60">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <div>
                  <div className="text-slate-200 font-medium">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.phone}</div>
                  <div
                    className="mt-1 text-[11px]"
                    data-testid={`client-credit-${c.id}`}
                  >
                    {Number(c.limit_kredit || 0) > 0 ? (
                      <span
                        className={
                          credit[c.id]?.blocked ? "text-rose-300" : "text-slate-400"
                        }
                      >
                        Limit kredit: <b>Rp {Number(c.limit_kredit).toLocaleString("id-ID")}</b> · terpakai{" "}
                        <b>Rp {Number(credit[c.id]?.terpakai || 0).toLocaleString("id-ID")}</b>
                        {credit[c.id]?.blocked ? " · LIMIT TERLAMPAUI (entri JO diblokir)" : ""}
                      </span>
                    ) : (
                      <span className="text-slate-600">Tanpa batas kredit</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`client-edit-btn-${c.id}`}
                    onClick={() => {
                      setEditId(c.id);
                      setDraft({ name: c.name, phone: c.phone, limit_kredit: c.limit_kredit || 0 });
                    }}
                    variant="outline"
                    className="h-9 w-9 p-0 rounded-lg border-white/10 bg-slate-800/60 hover:bg-blue-600/20"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`client-delete-btn-${c.id}`}
                    onClick={() => remove(c)}
                    variant="outline"
                    className="h-9 w-9 p-0 rounded-lg border-red-400/20 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
